import {
  DEFAULT_PUBLIC_CONFIG,
  PERSONAS,
  fmtDuration,
  fmtEth,
  fmtTokens,
  type AgentId,
  type ProtocolEvent,
  type PublicConfig,
} from '@quintessence/core';
import { useEffect, useState } from 'react';
import { useData } from './data/DataContext';

export function useSnapshot() {
  const data = useData();
  return {
    ...data,
    view: data.snapshot?.view ?? null,
    config: data.snapshot?.config ?? DEFAULT_PUBLIC_CONFIG,
  };
}

/** View feed plus anything that streamed in since, newest first, de-duplicated. */
export function mergeFeed(feed: ProtocolEvent[], streamed: ProtocolEvent[], limit = 60): ProtocolEvent[] {
  const byId = new Map<string, ProtocolEvent>();
  for (const e of [...streamed, ...feed]) if (!byId.has(e.id)) byId.set(e.id, e);
  return [...byId.values()]
    .filter((e) => e.type !== 'wait' && e.type !== 'learn' && e.type !== 'error')
    .sort((a, b) => b.t - a.t)
    .slice(0, limit);
}

export function ago(t: number, now: number): string {
  const d = now - t;
  if (d < 45_000) return 'just now';
  return `${fmtDuration(d).split(' ')[0]} ago`;
}

const ENV_NAME = (import.meta.env.VITE_PROTOCOL_NAME as string | undefined)?.trim();
const ENV_SYMBOL = (import.meta.env.VITE_TOKEN_SYMBOL as string | undefined)?.trim();

/** Protocol name and $TICKER. Build-time VITE_PROTOCOL_NAME / VITE_TOKEN_SYMBOL win over the runtime config. */
export function brand(config: PublicConfig): { name: string; symbol: string } {
  const ticker = (ENV_SYMBOL || config.symbol).replace(/^\$/, '');
  return { name: ENV_NAME || config.name, symbol: `$${ticker}` };
}

/** Shown while a figure is still loading. */
export const PENDING = '…';

export function sym(config: PublicConfig): string {
  return brand(config).symbol;
}

export function txUrl(config: PublicConfig, hash: string): string | null {
  return config.mode === 'live' && hash.startsWith('0x') ? `${config.explorerUrl}/tx/${hash}` : null;
}

/** The token's contract address. Shown until the runtime reports one (live mode), and then replaced by it. */
export const TOKEN_ADDRESS = '0xb1a6a77023c800cba6dc32abd13c9fdf508bd97e';

export function tokenAddress(config: PublicConfig): string {
  return config.tokenAddress || TOKEN_ADDRESS;
}

export function addrUrl(config: PublicConfig, addr: string): string {
  return `${config.explorerUrl}/address/${addr}`;
}

export function actionHeadline(e: Extract<ProtocolEvent, { type: 'action' }>, symbol: string): string {
  const r = e.result;
  switch (e.agent) {
    case 'pyre':
      return `burned ${fmtTokens(r.tokensBurned ?? r.tokensAcquired)} ${symbol}`;
    case 'hoard':
      return `bought back ${fmtTokens(r.tokensAcquired)} ${symbol}`;
    case 'rain':
      return `airdropped ${fmtTokens(r.tokensAcquired)} ${symbol} to ${fmtTokens(r.recipients ?? 0)} holders`;
    case 'tide':
      return `added ${fmtEth(r.ethToLiquidity ?? 0)} ETH + ${fmtTokens(r.tokensToLiquidity ?? 0)} ${symbol} of locked LP`;
  }
}

export const personaOf = (id: AgentId) => PERSONAS[id];

/** Re-renders on an interval; returns Date.now(). */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

export function pctChange(log: number): number {
  return Math.exp(log) - 1;
}
